import { getUser } from '../stores'
import { ChatLocalMesage, ChatUser, MessageGrouping, MessageStatusTypes } from '../types'
import type { LocalAttachment, Attachment as RemoteAttachment, StreamChat } from 'stream-chat'
import { attachmentPreviewText, getAttachmentLabel, getAttachmentFallbackText } from './attachments'
import { removeDuplicated } from './misc'

type Attachment = LocalAttachment | RemoteAttachment

type GetCustomMessageTextParams = {
  text?: string
  attachments?: Attachment[]
  pollName?: string
}

const getCustomMessageText = ({ text, attachments = [], pollName }: GetCustomMessageTextParams) => {
  const originalText = text || ''
  const messageText = originalText.trim()
  const attachment = attachments?.[0]

  if (pollName) {
    return `📊 ${pollName}`
  }

  if (messageText && attachment) {
    return `${getAttachmentLabel(attachment)} ${messageText}`
  }

  if (messageText) return messageText
  if (attachment) return getAttachmentFallbackText(attachment)

  return 'New message'
}

export type GetCustomMessageDataParams = GetCustomMessageTextParams & {
  mentionedUsers?: ChatUser[] | string[]
}

function getCustomMentionedUsers(mentionedUsers: string[]) {
  const filteredMentionedUsers = removeDuplicated(mentionedUsers)

  const mentionedUsersObject = filteredMentionedUsers.reduce((acc, userId, index) => {
    acc[`mentioned_user_${index + 1}`] = userId
    return acc
  }, {} as Record<string, string>)

  return mentionedUsersObject
} 

export const getCustomMessageData = (props: GetCustomMessageDataParams) => {

  const data = { 
    customMessage: getCustomMessageText(props), 
    ...(props.mentionedUsers?.length && getCustomMentionedUsers(props.mentionedUsers?.map(user => typeof user === 'string' ? user : user.id))),
  }
  return data
}

export function isMessageDeleted(message?: Pick<ChatLocalMesage, 'type'>) {
  if (message?.type === 'deleted') return true
}

export function isSystemMessage(message?: Pick<ChatLocalMesage, 'type'>) {
  if (message?.type === 'system') return true
}

type Action = {
  text: string
  rightIcon: string
  style?: string[]
}

const actionsMap = {
  reply: {
    text: 'Reply',
    rightIcon: 'message-text-circle-02',
  },
  forward: {
    text: 'Forward',
    rightIcon: 'share-06',
  },
  reminder: {
    text: 'Set reminder',
    rightIcon: 'clock-stopwatch',
  },
  edit: {
    text: 'Edit',
    rightIcon: 'pencil-line',
  },
  pin: {
    text: 'Pin',
    rightIcon: 'pin-01',
  },
  unpin: {
    text: 'Unpin',
    rightIcon: 'pin-blocked-02',
  },
  copy: {
    text: 'Copy',
    rightIcon: 'copy-05',
  },
  delete: {
    text: 'Delete',
    rightIcon: 'trash-03',
    style: ['list:destructive'],
  },
}

export type MessageActions = keyof typeof actionsMap

type ActionItem = Action & { id: MessageActions }

const ALL_ACTIONS: ActionItem[] = Object.entries(actionsMap).map(([id, action]) => ({
  id: id as MessageActions,
  ...action,
}))

export type GetMessageActionsProps = {
  isMyMessage: boolean
  isMessagePinned?: boolean
  isChatAdmin?: boolean
  isChatGroup?: boolean
  message: ChatLocalMesage
}

export function getMessageActions({isMyMessage, isChatAdmin, isMessagePinned, isChatGroup, message }: GetMessageActionsProps) {
  let actions = ALL_ACTIONS
  const hasText = !!message?.text?.trim()
  const isThread = isThreadMessage(message)
  const isPoll = isPollMessage(message)

  function removeAction(actionsIds: MessageActions[]) {
    const newActions =  actions.filter(action => !actionsIds.includes(action.id))
    actions = newActions
  }

  if (isPoll) {
    removeAction(['forward', 'edit', 'copy'])
  }

  if (isThread) {
    removeAction(['reply'])
  }

  if (isMessageForwarded(message)) {
    removeAction(['edit'])
  }

  if (!hasText) {
    removeAction(['edit', 'copy'])
  }

  if (isMessagePinned) {
    removeAction(['pin'])
  } else {
    removeAction(['unpin'])
  }

  if (isChatGroup && !isChatAdmin) {
    removeAction(['unpin', 'pin'])
  }
    
  if (!isMyMessage) {
    removeAction(['edit', 'delete'])
  }

  return actions
}

export function splitAggregatedSystemMessage(message: ChatLocalMesage): ChatLocalMesage[] {
  // @ts-ignore
  const targets = Array.isArray(message?.custom?.targets) ? message.custom.targets : []
  if (targets.length < 1) return [message]

  // @ts-ignore
  const action = message?.custom?.action
  const defaultTextByAction: Record<string, string> = {
    members_added: 'added',
    moderators_granted: 'granted admin to',
  }

  return targets.map((target: { id?: string, name?: string }) => {
    // @ts-ignore
    const custom = {
      // @ts-ignore
      ...(message?.custom || {}),
      target_id: target?.id,
      target_name: target?.name,
    }

    delete custom.targets
    delete custom.count

    return {
      ...message,
      text: defaultTextByAction[action] || message?.text,
      custom,
    } as ChatLocalMesage
  })
}

export function messageSystemText(message: ChatLocalMesage) {
  // If this is an aggregated system message, preview should match
  // the last generated split message so chat list text mirrors UI ordering.
  // @ts-ignore
  const hasTargets = Array.isArray(message?.custom?.targets) && message.custom.targets.length > 0
  if (hasTargets) {
    const splitMessages = splitAggregatedSystemMessage(message)
    const lastGeneratedMessage = splitMessages[splitMessages.length - 1]
    if (lastGeneratedMessage) {
      return messageSystemText(lastGeneratedMessage)
    }
  }

  // @ts-ignore
  const targetId = message?.custom?.target_id
  // @ts-ignore
  const targetName = message?.custom?.target_name
  const user = getUser()
  const userName = message?.user?.id === user?.id ? 'You' : message?.user?.name || 'Someone'
  const targetUserName = targetId ? ` ${targetId === user?.id ? 'You' : targetName}` : ''
  const messageText = `${userName} ${message?.text}${targetUserName}`
  return messageText
}

type UseMessageGroupingProps = {
  message: ChatLocalMesage
  groupStyles: string[]
  lastGroupMessage?: boolean
}

let prevMessage : ChatLocalMesage | null = null

function savePrevMessage(message: ChatLocalMesage) {
  prevMessage = message
}

export function useMessageGrouping({ message, groupStyles = [], lastGroupMessage: lastGroupMessageProp }: UseMessageGroupingProps): MessageGrouping {
  const groupStylesKey = groupStyles?.join(',') || ''

  // const grouping = useMemo(() => {
  //   let lastGroupMessage = lastGroupMessageProp || groupStyles?.some(style => ['bottom', 'single'].includes(style))
  //   let firstGroupMessage = !groupStyles ? false : groupStyles?.some(style => ['top', 'single'].includes(style))

  //   const shouldCompare = (
  //     prevMessage &&
  //     prevMessage?.cid === message?.cid &&
  //     prevMessage?.id !== message?.id &&
  //     !lastGroupMessage
  //   )

  //   if (shouldCompare) {
  //     const sameUser = message.user?.id === prevMessage.user?.id
  //     const currentMessageTime = new Date(message.created_at)
  //     const prevMessageTime = new Date(prevMessage.created_at)

  //     const sameMinute = currentMessageTime.getFullYear() === prevMessageTime.getFullYear() &&
  //       currentMessageTime.getMonth() === prevMessageTime.getMonth() &&
  //       currentMessageTime.getDate() === prevMessageTime.getDate() &&
  //       currentMessageTime.getHours() === prevMessageTime.getHours() &&
  //       currentMessageTime.getMinutes() === prevMessageTime.getMinutes()
      
  //     if (!(sameMinute && sameUser)) {
  //       lastGroupMessage = true
  //     }
  //   }

  //   savePrevMessage(message)
  //   return { lastGroupMessage, firstGroupMessage }
  // }, [groupStylesKey, lastGroupMessageProp])

  const isDeleted = isMessageDeleted(message)

  if (isDeleted) {
    return {
      lastGroupMessage: true,
      firstGroupMessage: true,
    }
  }

  return {
    lastGroupMessage: lastGroupMessageProp || groupStyles?.some(style => ['bottom', 'single'].includes(style)),
    firstGroupMessage: !groupStyles ? false : groupStyles?.some(style => ['top', 'single'].includes(style))
  }
}

export function isMessageEdited(message: ChatLocalMesage) {
  return !!message?.message_text_updated_at
}

export function isMessageForwarded(message: ChatLocalMesage) {
  // @ts-ignore
  return !!message?.custom?.forwarded
}

export function isThreadMessage(message: ChatLocalMesage) {
  return !!message?.parent_id
}

export function isThreadParentMessage(message: ChatLocalMesage, threadList: boolean) {
  return  threadList && !message?.parent_id
}

export async function getFreshMessage(client: StreamChat, message: ChatLocalMesage) {
  if (message.status !== MessageStatusTypes.SENDING) return message

  const updatedMessageResponse = await client.getMessage(message.id)
  const finalMessage = updatedMessageResponse.message
  const freshMessage = {
    ...finalMessage,
    created_at: new Date(finalMessage.created_at as string),
    updated_at: new Date(finalMessage.updated_at as string),
    deleted_at: finalMessage.deleted_at ? new Date(finalMessage.deleted_at as string) : undefined,
    pinned_at: finalMessage.pinned_at ? new Date(finalMessage.pinned_at as string) : undefined,
  } as unknown as ChatLocalMesage

  return freshMessage
}

export function isPollMessage(message: ChatLocalMesage) {
  return !!message?.poll_id
}

export function normalizeMessageText(input: string): string | null {
  const trimmed = input?.trim()
  if (!trimmed) return null

  const isOnlyMarkdown = /^[\s\-_*]+$/.test(trimmed)

  if (isOnlyMarkdown) {
    return `\u200B${trimmed}`
  }

  return trimmed
}

export function pinnedMessagePreviewText(message: ChatLocalMesage) {
  const attachment = message?.attachments?.[0]
  const hasText = !!message?.text?.trim()
  const poll = message?.poll

  if (poll) {
    return poll.name
  }

  if (hasText) {
    return message?.text 
  }

  if (attachment) return attachmentPreviewText(attachment)
  return null
}

export function messageHasAudio(message: ChatLocalMesage) {
  return message?.attachments?.some(
    attachment => attachment?.type === 'audio' || attachment?.type === 'voiceRecording',
  )
}

export function getMessageUserName(message: ChatLocalMesage) {
  return message?.user?.name || 'Unknown User'
}
